/**
 * ModuleDirectoryCard — at-a-glance view of which cross-scope modules are
 * enabled for this tenant and how many records each holds.
 *
 * Two-click enable/disable per module. Reads `/module-directory` and writes
 * via PUT `/module-directory/{module_key}`.
 *
 * When disabling a module that still has records, shows a confirmation dialog
 * so the admin is clearly reminded that data is preserved — only the tab
 * visibility changes.
 */
import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Switch } from '@/components/ui/switch';
import { Badge } from '@/components/ui/badge';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Loader2, LayoutGrid, AlertTriangle } from 'lucide-react';
import { toast } from 'sonner';
import { authHeaders } from '@/lib/authToken';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const ModuleDirectoryCard = ({ tenantId }) => {
  const qc = useQueryClient();
  const [confirmDisable, setConfirmDisable] = useState(null); // { key, label, record_count }

  const { data, isLoading } = useQuery({
    queryKey: ['module-directory', tenantId],
    queryFn: async () => {
      const r = await fetch(`${API_URL}/api/school/tenants/${tenantId}/module-directory`, {
        headers: authHeaders(),
      });
      if (!r.ok) throw new Error('Failed to load module directory');
      return r.json();
    },
    enabled: !!tenantId,
  });

  const toggle = useMutation({
    mutationFn: async ({ key, enabled }) => {
      const r = await fetch(`${API_URL}/api/school/tenants/${tenantId}/module-directory/${key}`, {
        method: 'PUT',
        headers: { ...authHeaders(), 'Content-Type': 'application/json' },
        body: JSON.stringify({ enabled }),
      });
      if (!r.ok) {
        const err = await r.json().catch(() => ({}));
        throw new Error(err.detail || 'Failed to update module');
      }
      return r.json();
    },
    onSuccess: (res) => {
      toast.success(`${res.module}: ${res.enabled ? 'enabled' : 'disabled'}`);
      qc.invalidateQueries({ queryKey: ['module-directory', tenantId] });
    },
    onError: (e) => toast.error(e.message),
  });

  const summary = data?.summary;
  const modules = data?.modules || [];

  return (
    <Card data-testid="module-directory-card" className="border-slate-200">
      <CardHeader className="pb-3">
        <div className="flex items-start justify-between flex-wrap gap-2">
          <div>
            <CardTitle className="text-base font-semibold flex items-center gap-2">
              <LayoutGrid className="h-4 w-4 text-indigo-600" />
              Module Directory
            </CardTitle>
            <CardDescription className="text-xs mt-0.5">
              Cross-scope modules available to this school. Toggling off only hides the tab — no data is deleted.
            </CardDescription>
          </div>
          {summary && (
            <div className="flex items-center gap-1.5">
              <Badge className="bg-emerald-100 text-emerald-700 border-emerald-200 text-[10px]" data-testid="modules-enabled-badge">
                {summary.enabled_count} enabled
              </Badge>
              <Badge className="bg-indigo-100 text-indigo-700 border-indigo-200 text-[10px]" data-testid="modules-active-badge">
                {summary.active_count} active
              </Badge>
            </div>
          )}
        </div>
      </CardHeader>
      <CardContent>
        {isLoading ? (
          <div className="flex items-center justify-center py-8">
            <Loader2 className="h-4 w-4 animate-spin text-slate-400" />
          </div>
        ) : (
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-2">
            {modules.map((m) => (
              <div
                key={m.key}
                className={`rounded-lg border p-3 transition ${
                  m.enabled
                    ? (m.record_count > 0 ? 'bg-emerald-50/40 border-emerald-200' : 'bg-white border-slate-200')
                    : 'bg-slate-50 border-slate-200 opacity-60'
                }`}
                data-testid={`module-tile-${m.key}`}
              >
                <div className="flex items-start justify-between gap-2">
                  <div className="min-w-0">
                    <div className="flex items-center gap-1.5">
                      <span className="text-base">{m.icon}</span>
                      <span className="text-sm font-semibold truncate">{m.label}</span>
                    </div>
                    <div className="text-[11px] text-slate-500 mt-0.5">
                      <span className="font-mono" data-testid={`module-count-${m.key}`}>{m.record_count.toLocaleString()}</span> record{m.record_count === 1 ? '' : 's'}
                    </div>
                  </div>
                  <Switch
                    checked={m.enabled}
                    onCheckedChange={(v) => {
                      // Disabling a module that has records → confirm first
                      if (!v && m.record_count > 0) {
                        setConfirmDisable({ key: m.key, label: m.label, record_count: m.record_count });
                        return;
                      }
                      toggle.mutate({ key: m.key, enabled: v });
                    }}
                    disabled={toggle.isPending}
                    data-testid={`module-toggle-${m.key}`}
                  />
                </div>
              </div>
            ))}
          </div>
        )}
      </CardContent>

      <AlertDialog open={!!confirmDisable} onOpenChange={(o) => !o && setConfirmDisable(null)}>
        <AlertDialogContent data-testid="module-disable-confirm-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2">
              <AlertTriangle className="h-5 w-5 text-amber-500" />
              Disable {confirmDisable?.label}?
            </AlertDialogTitle>
            <AlertDialogDescription className="space-y-2">
              <span className="block">
                This school has{' '}
                <strong data-testid="module-disable-confirm-count">
                  {confirmDisable?.record_count?.toLocaleString()}
                </strong>{' '}
                {confirmDisable?.label?.toLowerCase()} record
                {confirmDisable?.record_count === 1 ? '' : 's'}.
              </span>
              <span className="block text-emerald-700 font-medium">
                Disabling only hides the {confirmDisable?.label} tab from the admin UI. No data will be deleted — you can re-enable anytime to see everything again.
              </span>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="module-disable-confirm-cancel">Keep enabled</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => {
                if (confirmDisable) {
                  toggle.mutate({ key: confirmDisable.key, enabled: false });
                  setConfirmDisable(null);
                }
              }}
              className="bg-amber-600 hover:bg-amber-700"
              data-testid="module-disable-confirm-proceed"
            >
              Hide tab
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </Card>
  );
};

export default ModuleDirectoryCard;
